<template>
  <MysticalCard class="ai-result-card">
    <template #header>
      <div class="card-header">
        <div class="header-left">
          <i class="iconfont" :class="iconClass"></i>
          <div class="header-titles">
            <h3 class="title">{{ title }}</h3>
            <p class="subtitle">{{ subtitle }}</p>
          </div>
        </div>
        <div class="header-right">
          <span class="badge" :class="{ 'loading': loading }">
            {{ loading ? '分析中...' : status }}
          </span>
        </div>
      </div>
    </template>
    
    <div class="result-content" 
         :class="{ 'loading': loading }">
      <div class="markdown-content" v-html="renderedContent"></div>
    </div>
  </MysticalCard>
</template>

<script setup>
import { computed, watch, ref } from 'vue'
import MysticalCard from '@/components/common/MysticalCard.vue'
import { marked } from 'marked'

// 配置 marked 选项
marked.setOptions({
  gfm: true,
  breaks: true,
  mangle: false,
  headerIds: false,
  smartLists: true,
  smartypants: true,
  headerPrefix: 'section-'
})

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    required: true
  },
  content: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  status: {
    type: String,
    default: '分析完成'
  }
})

// 计算图标类名
const iconClass = computed(() => {
  return `icon-${props.icon}`
})

// 渲染 Markdown 内容
const renderedContent = computed(() => {
  if (!props.content) return ''
  
  try {
    // 预处理内容
    let processedContent = props.content
      // 确保标题格式正确
      .replace(/^(#+ )([^\n]+)/gm, '$1$2')
      // 确保列表项格式正确
      .replace(/^[-*] /gm, '- ')
      // 确保段落之间有空行
      .replace(/([^\n])\n([^\n])/g, '$1\n\n$2')
      // 确保换行符后面有两个空格
      .replace(/\n/g, '  \n')
      // 移除回车符
      .replace(/\r/g, '')
      // 处理特殊标点
      .replace(/([：，。！？；])/g, '$1 ')
      
    return marked(processedContent)
  } catch (error) {
    console.error('Markdown 渲染错误:', error)
    return `<pre>${props.content}</pre>`
  }
})
</script>

<style scoped>
.ai-result-card {
  height: 100%;
  transition: all 0.3s ease;
}

.ai-result-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px var(--fh-shadow-medium);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--color-border);
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.header-left .iconfont {
  font-size: 24px;
  color: var(--color-primary);
}

.header-titles {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-titles .title {
  font-size: 1.25rem;
  color: var(--color-primary);
  margin: 0;
  font-family: var(--font-mystical);
  font-weight: 600;
}

.header-titles .subtitle {
  font-size: 0.9rem;
  color: var(--color-text-light);
  margin: 0;
}

.badge {
  padding: 4px 12px;
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  background: var(--color-accent);
  color: var(--color-light);
  font-weight: 500;
  transition: all 0.3s ease;
}

.badge.loading {
  background: var(--color-primary);
  animation: pulse 1.5s infinite;
}

.result-content {
  padding: var(--spacing-lg);
  line-height: 1.8;
  min-height: 200px;
  transition: opacity 0.3s ease;
}

.result-content.loading {
  opacity: 0.7;
  position: relative;
}

.result-content.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-accent);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

@keyframes spin {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

.markdown-content {
  font-family: var(--font-text);
  line-height: 1.8;
  color: var(--color-text);
  padding: 1rem;
}

.markdown-content :deep(h1) {
  font-size: 1.8rem;
  color: var(--color-primary);
  margin: 1.5rem 0 1rem;
  border-bottom: 2px solid var(--color-primary);
  padding-bottom: 0.5rem;
}

.markdown-content :deep(h2) {
  font-size: 1.4rem;
  color: var(--color-primary);
  margin: 1.5rem 0 1rem;
  padding-bottom: 0.3rem;
  border-bottom: 1px solid var(--color-border);
}

.markdown-content :deep(p) {
  margin: 1rem 0;
  line-height: 1.8;
  color: var(--color-text);
  text-align: justify;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin: 1rem 0;
  padding-left: 2rem;
}

.markdown-content :deep(li) {
  margin: 0.5rem 0;
  line-height: 1.6;
  color: var(--color-text);
}

.markdown-content :deep(li)::marker {
  color: var(--color-primary);
}

.markdown-content :deep(blockquote) {
  margin: 1rem 0;
  padding: 0.5rem 1rem;
  border-left: 4px solid var(--color-primary);
  background: var(--color-background-light);
  color: var(--color-text);
}
</style> 